import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { PoundSterling, ShoppingBag, CheckCircle2, RefreshCw, LogOut } from "lucide-react";
import { fetchAdminOrders, gbp } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-tan bg-offwhite p-6">
    <div className="flex items-center gap-3 text-faded mb-4">
      <Icon className="w-5 h-5" strokeWidth={1.5} />
      <span className="text-xs uppercase tracking-[0.2em] font-bold">{label}</span>
    </div>
    <p className="font-sans font-black text-3xl text-ink">{value}</p>
  </div>
);

export default function Admin() {
  const { admin, logout } = useAuth();
  const [data, setData] = useState({ orders: [], stats: {} });
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    fetchAdminOrders()
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => {
        setLoading(false);
        if (e.response?.status === 401) logout();
      });
  };

  useEffect(() => {
    if (admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);

  if (admin === null) {
    return <div className="min-h-screen bg-cream flex items-center justify-center font-serif italic text-faded text-xl" data-testid="admin-loading">Checking access…</div>;
  }
  if (admin === false) {
    return <Navigate to="/admin/login" replace />;
  }

  const { stats } = data;

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <Link to="/" className="flex flex-col leading-none">
              <span className="font-sans font-black tracking-[0.14em] text-lg text-ink">PRESTIGE</span>
              <span className="font-serif italic text-sm tracking-[0.28em] text-faded">wellness co. — admin</span>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={load} className="flex items-center gap-2 rounded-full bg-forest text-cream px-5 py-3 text-xs font-bold tracking-widest uppercase hover:bg-forest-deep transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={2} /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 rounded-full border border-forest/30 text-forest px-5 py-3 text-xs font-bold tracking-widest uppercase hover:bg-tan transition-colors">
              <LogOut className="w-4 h-4" strokeWidth={2} /> Logout
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-5 mb-12">
          <StatCard testid="stat-revenue" icon={PoundSterling} label="Paid revenue" value={gbp(stats.total_revenue || 0)} />
          <StatCard testid="stat-orders" icon={ShoppingBag} label="Total orders" value={stats.total_orders || 0} />
          <StatCard testid="stat-paid" icon={CheckCircle2} label="Paid orders" value={stats.paid_orders || 0} />
        </div>

        <div className="rounded-2xl border border-tan bg-offwhite overflow-hidden">
          <div className="px-6 py-5 border-b border-tan">
            <h2 className="font-sans font-extrabold text-xl tracking-tight text-ink">Orders</h2>
          </div>
          <div className="overflow-x-auto">
            <Table data-testid="orders-table">
              <TableHeader>
                <TableRow className="border-tan hover:bg-transparent">
                  <TableHead className="text-faded">Order</TableHead>
                  <TableHead className="text-faded">Email</TableHead>
                  <TableHead className="text-faded">Items</TableHead>
                  <TableHead className="text-faded">Total</TableHead>
                  <TableHead className="text-faded">Status</TableHead>
                  <TableHead className="text-faded">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.orders.length === 0 && !loading && (
                  <TableRow className="border-tan hover:bg-transparent">
                    <TableCell colSpan={6} className="text-center text-faded py-10 font-serif italic">No orders yet.</TableCell>
                  </TableRow>
                )}
                {data.orders.map((o) => (
                  <TableRow key={o.order_id} data-testid={`order-row-${o.order_id}`} className="border-tan hover:bg-tan/30">
                    <TableCell className="font-mono text-xs text-faded">{o.order_id.slice(0, 8)}</TableCell>
                    <TableCell className="text-ink">{o.email || "—"}</TableCell>
                    <TableCell className="text-faded text-sm max-w-[220px] truncate">
                      {o.items?.map((it) => `${it.quantity}× ${it.name}`).join(", ")}
                    </TableCell>
                    <TableCell className="font-bold text-ink">{gbp(o.amount)}</TableCell>
                    <TableCell>
                      <Badge className={`${o.payment_status === "paid" ? "bg-forest text-cream" : "bg-tan text-faded"} border-0 rounded-full`}>
                        {o.payment_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-faded text-sm">{new Date(o.created_at).toLocaleDateString("en-GB")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
